/**
 * ProjectAgentOverrides
 * Shows workspace agent config cascading to project level.
 * Each field is either "inherited" (from workspace) or "overridden" (project-specific).
 * Uses InheritedField for visual hierarchy.
 */

import { useState, useCallback, useEffect } from 'react';
import { Save, RotateCcw, Check } from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAgentConfig, type AgentConfig } from '../../api/workspace-agent-config';
import { useDevSettings, useUpdateDevSettings } from '../../api/dev-settings';
import { InheritedField, FieldGroup } from '../build-config/InheritedField';
import { Panel, PanelHeader, PanelBody } from '../shared/Panel';

type OverrideMap = Record<string, any>;

function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((o, k) => o?.[k], obj);
}

function setNestedValue(obj: any, path: string, value: any): any {
  const keys = path.split('.');
  const result = { ...obj };
  let current = result;
  for (let i = 0; i < keys.length - 1; i++) {
    current[keys[i]] = { ...current[keys[i]] };
    current = current[keys[i]];
  }
  current[keys[keys.length - 1]] = value;
  return result;
}

function deleteNestedValue(obj: any, path: string): any {
  const keys = path.split('.');
  const result = { ...obj };
  let current = result;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!current[keys[i]]) return result;
    current[keys[i]] = { ...current[keys[i]] };
    current = current[keys[i]];
  }
  delete current[keys[keys.length - 1]];
  return result;
}

// ─── OverridableTextField ────────────────────────────────────

function OverridableTextField({
  label,
  path,
  workspaceValue,
  overrides,
  onChange,
  onClear,
  rows = 4,
}: {
  label: string;
  path: string;
  workspaceValue: string;
  overrides: OverrideMap;
  onChange: (path: string, value: string) => void;
  onClear: (path: string) => void;
  rows?: number;
}) {
  const overrideValue = getNestedValue(overrides, path);
  const isOverridden = overrideValue !== undefined;
  const displayValue = isOverridden ? overrideValue : workspaceValue;

  return (
    <InheritedField
      label={label}
      source={isOverridden ? 'project' : 'workspace'}
      isOverridden={isOverridden}
      inheritedValue={workspaceValue ? (workspaceValue.length > 120 ? workspaceValue.slice(0, 120) + '...' : workspaceValue) : '(empty)'}
      onClearOverride={() => onClear(path)}
    >
      <textarea
        value={displayValue ?? ''}
        onChange={(e) => onChange(path, e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 border border-line rounded-lg text-text bg-white font-mono text-[12px] leading-relaxed focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none resize-y"
      />
    </InheritedField>
  );
}

// ─── OverridableListField ────────────────────────────────────

function OverridableListField({
  label,
  path,
  workspaceValue,
  overrides,
  onChange,
  onClear,
}: {
  label: string;
  path: string;
  workspaceValue: string[];
  overrides: OverrideMap;
  onChange: (path: string, value: string[]) => void;
  onClear: (path: string) => void;
}) {
  const overrideValue = getNestedValue(overrides, path) as string[] | undefined;
  const isOverridden = overrideValue !== undefined;
  const displayValue = isOverridden ? overrideValue : workspaceValue;
  const [newItem, setNewItem] = useState('');

  return (
    <InheritedField
      label={label}
      source={isOverridden ? 'project' : 'workspace'}
      isOverridden={isOverridden}
      inheritedValue={workspaceValue?.length ? `${workspaceValue.length} item(s)` : '(empty)'}
      onClearOverride={() => onClear(path)}
    >
      <div className="space-y-1.5">
        {(displayValue ?? []).map((item, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span className="flex-1 px-2 py-1 border border-line rounded text-[12px] font-mono bg-white">
              {item}
            </span>
            <button
              onClick={() => {
                const updated = [...(displayValue ?? [])];
                updated.splice(i, 1);
                onChange(path, updated);
              }}
              className="p-0.5 text-subtle hover:text-red-500 transition-colors"
            >
              &times;
            </button>
          </div>
        ))}
        <div className="flex gap-1.5">
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newItem.trim()) {
                onChange(path, [...(displayValue ?? []), newItem.trim()]);
                setNewItem('');
              }
            }}
            placeholder="Add item..."
            className="flex-1 px-2 py-1 border border-line rounded text-[12px] font-mono focus:ring-1 focus:ring-coral-400 outline-none"
          />
          <button
            onClick={() => {
              if (newItem.trim()) {
                onChange(path, [...(displayValue ?? []), newItem.trim()]);
                setNewItem('');
              }
            }}
            className="px-2 py-1 text-[11px] text-coral-600 hover:text-coral-700 font-medium"
          >
            + Add
          </button>
        </div>
      </div>
    </InheritedField>
  );
}

// ─── Main Component ─────────────────────────────────────────

export function ProjectAgentOverrides() {
  const { project } = useProject();
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;
  const projectId = project?.id;

  const { data: wsConfig, isLoading: loadingWs } = useAgentConfig(workspaceId);
  const { data: devSettings, isLoading: loadingDev } = useDevSettings(projectId);
  const updateDevSettings = useUpdateDevSettings();

  // Local overrides tracked as a nested object mirroring agentConfig structure
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Sync from devSettings on load
  useEffect(() => {
    if (devSettings) {
      setOverrides((devSettings as any).agentConfig ?? {});
    }
  }, [devSettings]);

  const handleChange = useCallback((path: string, value: any) => {
    setOverrides(prev => setNestedValue(prev, path, value));
    setSaveStatus('idle');
  }, []);

  const handleClear = useCallback((path: string) => {
    setOverrides(prev => deleteNestedValue(prev, path));
    setSaveStatus('idle');
  }, []);

  const hasChanges = JSON.stringify(overrides) !== JSON.stringify((devSettings as any)?.agentConfig ?? {});

  const handleSave = useCallback(async () => {
    if (!projectId || !hasChanges) return;
    setSaveStatus('saving');
    try {
      await updateDevSettings.mutateAsync({
        projectId,
        settings: { agentConfig: overrides } as any,
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('idle');
    }
  }, [projectId, hasChanges, overrides, updateDevSettings]);

  if (!project) {
    return (
      <Panel>
        <PanelBody>
          <div className="text-center py-8 text-muted text-sm">
            Select a project to configure agent overrides.
          </div>
        </PanelBody>
      </Panel>
    );
  }

  if (loadingWs || loadingDev) {
    return (
      <div className="space-y-4">
        {[1, 2].map(i => <div key={i} className="h-40 bg-surface-subtle rounded-lg animate-pulse" />)}
      </div>
    );
  }

  const ws = wsConfig ?? {} as AgentConfig;

  return (
    <div className="space-y-6">
      <div className="px-1 text-[12px] text-muted">
        Project-level overrides cascade from workspace agent configuration. Override a field to customize it for <span className="font-semibold text-text">{project.name}</span>, or leave inherited to use the workspace default.
      </div>

      {/* Sprite Instructions */}
      <FieldGroup title="Sprite Instructions">
        <OverridableTextField
          label="CLAUDE.md Template"
          path="spriteInstructions.claudeMdTemplate"
          workspaceValue={ws.spriteInstructions?.claudeMdTemplate ?? ''}
          overrides={overrides}
          onChange={handleChange}
          onClear={handleClear}
          rows={8}
        />
        <OverridableTextField
          label="Additional Instructions"
          path="spriteInstructions.additionalInstructions"
          workspaceValue={ws.spriteInstructions?.additionalInstructions ?? ''}
          overrides={overrides}
          onChange={handleChange}
          onClear={handleClear}
          rows={3}
        />
        <OverridableListField
          label="Code Conventions"
          path="spriteInstructions.codeConventions"
          workspaceValue={ws.spriteInstructions?.codeConventions ?? []}
          overrides={overrides}
          onChange={handleChange}
          onClear={handleClear}
        />
        <OverridableListField
          label="Common Failures"
          path="spriteInstructions.commonFailures"
          workspaceValue={ws.spriteInstructions?.commonFailures ?? []}
          overrides={overrides}
          onChange={handleChange}
          onClear={handleClear}
        />
      </FieldGroup>

      {/* Code Execution Prompts */}
      <FieldGroup title="Code Execution Prompts">
        <OverridableTextField
          label="System Persona"
          path="codeExecution.systemPromptPersona"
          workspaceValue={ws.codeExecution?.systemPromptPersona ?? ''}
          overrides={overrides}
          onChange={handleChange}
          onClear={handleClear}
          rows={3}
        />
        <OverridableTextField
          label="Project Context"
          path="codeExecution.projectContext"
          workspaceValue={ws.codeExecution?.projectContext ?? ''}
          overrides={overrides}
          onChange={handleChange}
          onClear={handleClear}
          rows={4}
        />
        <OverridableTextField
          label="Development Standards"
          path="codeExecution.developmentStandards"
          workspaceValue={ws.codeExecution?.developmentStandards ?? ''}
          overrides={overrides}
          onChange={handleChange}
          onClear={handleClear}
          rows={3}
        />
      </FieldGroup>

      {/* Save Bar */}
      {hasChanges && (
        <div className="sticky bottom-4 z-10">
          <div className="bg-white border border-line rounded-xl shadow-lg px-4 py-3 flex items-center justify-between">
            <span className="text-[13px] text-muted">Unsaved project overrides</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setOverrides((devSettings as any)?.agentConfig ?? {}); setSaveStatus('idle'); }}
                className="px-3 py-1.5 text-[13px] text-muted hover:text-text rounded-lg hover:bg-surface-subtle transition-colors"
              >
                <RotateCcw size={14} className="inline mr-1" />
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={saveStatus === 'saving'}
                className="px-4 py-1.5 text-[13px] font-medium text-white bg-coral-500 hover:bg-coral-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {saveStatus === 'saving' ? (
                  <span className="inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : saveStatus === 'saved' ? (
                  <Check size={14} />
                ) : (
                  <Save size={14} />
                )}
                {saveStatus === 'saved' ? 'Saved' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
